import { useCallback } from "react";
import { Card, Skeleton, Stack, Typography } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useAppState } from "@/modules/app/useAppContext";
import { useChartData, useSkuCostBreakdown } from "../hooks";
import { BREAKDOWN_CONFIG, BreakdownGroupBy } from "../constants";
import CostBreakdownTable from "./CostBreakdownTable";

interface Props {
  startDate: string;
  endDate: string;
  workspaceId?: string;
  groupBy: BreakdownGroupBy;
  className?: string;
  noCard?: boolean;
}

/** URL search-param key used for the multi-value name filter, per tab. */
const FILTER_KEY: Record<BreakdownGroupBy, string> = {
  sku: "skuName",
  product: "productName",
};

const BreakdownChart = ({
  startDate,
  endDate,
  workspaceId,
  groupBy,
  className = "",
  noCard = false,
}: Props) => {
  const config = BREAKDOWN_CONFIG[groupBy];
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();

  const sortAttribute = searchParams.get("sortAttribute") || undefined;
  const rawSortOrder = searchParams.get("sortOrder");
  const sortOrder: "asc" | "desc" | undefined =
    rawSortOrder === "asc" || rawSortOrder === "desc" ? rawSortOrder : undefined;
  const filterKey = FILTER_KEY[groupBy];
  const nameFilter = searchParams.getAll(filterKey);

  const handleFilterChange = useCallback(
    (values: string[]) => {
      updateSearchParamsByKey({ [filterKey]: values });
    },
    [updateSearchParamsByKey, filterKey]
  );

  const handleRemoveFilter = useCallback(
    (value: string) => {
      deleteSearchParamsByKeyValue({ [filterKey]: value });
    },
    [deleteSearchParamsByKeyValue, filterKey]
  );

  const { data, isLoading, error } = useSkuCostBreakdown({
    start_date: startDate,
    end_date: endDate,
    workspace_id: workspaceId,
    group_by: groupBy,
    limit: 20,
    sortAttribute,
    sortOrder,
    ...(groupBy === "sku"
      ? { sku_names: nameFilter.length > 0 ? nameFilter : undefined }
      : { products: nameFilter.length > 0 ? nameFilter : undefined }),
  });

  const otherLabelFn = useCallback(
    (count: number) => `Other (${count}${config.otherLabelSuffix})`,
    [config.otherLabelSuffix]
  );

  const { sortedBreakdown } = useChartData({
    breakdown: data?.breakdown ?? [],
    otherLabel: otherLabelFn,
  });

  if (isLoading) {
    const inner = (
      <Stack direction="column" gap="md">
        <Skeleton className="al-h-48 al-w-full" />
      </Stack>
    );
    return noCard ? inner : <Card className={`al-p-4 ${className}`}>{inner}</Card>;
  }

  if (error || !data) {
    const inner = (
      <Typography className="al-text-red-500">
        Failed to load {config.label.toLowerCase()} breakdown
      </Typography>
    );
    return noCard ? inner : <Card className={`al-p-4 ${className}`}>{inner}</Card>;
  }

  const inner = (
    <>
      <CostBreakdownTable
        items={sortedBreakdown}
        currency={currency}
        columnLabel={config.label}
        nameFilter={nameFilter}
        onFilterChange={handleFilterChange}
        onRemoveFilter={handleRemoveFilter}
      />

      {sortedBreakdown.length === 0 && (
        <Typography className="al-text-gray-500 al-text-center al-py-8">
          No {config.label.toLowerCase()} data available
        </Typography>
      )}
    </>
  );

  return noCard ? inner : <Card className={`al-p-4 ${className}`}>{inner}</Card>;
};

export default BreakdownChart;
